"use client";

import { useSyncExternalStore } from "react";
import { useSession, signIn, signOut } from "@/lib/auth/client";
import { Button } from "@/components/ui/button";
import { LogIn, Coins, LogOut, User } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

import Link from "next/link";
import { useCredits } from "@/hooks/use-credits";
import { usePathname, useRouter } from "next/navigation";
import { trackGalleryCtaClicked } from "@/lib/posthog-client";
import { cn } from "@/lib/utils";

const emptySubscribe = () => () => {};

export function Header() {
  const mounted = useSyncExternalStore(emptySubscribe, () => true, () => false);
  const { data: session, isPending } = useSession();
  const { data: credits } = useCredits();
  const pathname = usePathname();
  const router = useRouter();
  const isAppPage = pathname.startsWith("/app");
  
  // Low credits threshold (minimum needed for lowest resolution)
  const creditBalance = credits?.balance ?? 0;
  const isLowCredits = creditBalance < 1;

  // Show skeleton during SSR and initial client render to prevent hydration mismatch
  const showSkeleton = !mounted || isPending;

  return (
    <header 
      className="sticky top-0 z-50 bg-[#080A0D]/95 backdrop-blur-xl border-b border-white/5 transition-all duration-300 w-full"
      aria-label="Site header"
    >
      <div className={cn(
        "w-full px-4 sm:px-6 flex h-14 sm:h-16 items-center justify-between",
        isAppPage ? "" : "max-w-7xl mx-auto"
      )}>
        <Link 
          href="/" 
          onClick={(e) => {
            e.preventDefault();
            router.push("/");
          }}
          className="flex items-center space-x-2 shrink-0"
          aria-label="The Wheel Visualizer - Home"
          title="The Wheel Visualizer - Home"
        >
          <img src="/logo-rim.png" alt="The Wheel Visualizer" className="h-7 sm:h-8 w-auto" />
          {/* Mobile: Short text, Desktop: Full text */}
          <span className="font-bold text-white tracking-tight">
            <span className="sm:hidden text-base">Wheel Visualizer</span>
            <span className="hidden sm:inline text-xl">
              <span className="italic">The</span> Wheel Visualizer
            </span>
          </span>
        </Link>

        <nav className="flex items-center gap-2 sm:gap-4">
          <Link
            href="/wheel-guide"
            className="hidden md:block text-sm text-white/60 hover:text-white transition-colors"
          >
            Wheel Guide
          </Link>
          <Link
            href="/gallery"
            className="hidden sm:block text-sm text-white/60 hover:text-white transition-colors"
            onClick={() => trackGalleryCtaClicked({ location: "header" })}
          >
            Gallery
          </Link>
          <Link
            href="/for-dealers"
            className="hidden lg:block text-sm text-white/60 hover:text-white transition-colors"
          >
            For Dealers
          </Link>
          {showSkeleton ? (
            <div className="h-8 w-20 bg-white/5 animate-pulse rounded-lg" />
          ) : session?.user ? (
            <>
              <Link
                href="/credits"
                className={cn(
                  "flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-lg text-sm transition-all",
                  isLowCredits 
                    ? "text-amber-400 hover:text-amber-300 hover:bg-amber-500/10 animate-pulse" 
                    : "text-white/60 hover:text-white hover:bg-white/5"
                )}
              >
                <Coins className="w-4 h-4" />
                <span>{creditBalance}</span>
              </Link>
              {!isAppPage && (
                <Link 
                  href="/app" 
                  onClick={(e) => {
                    e.preventDefault();
                    router.push("/app");
                  }}
                  className="hidden sm:block"
                >
                  <Button size="sm" className="bg-white text-black hover:bg-white/90 font-medium">
                    Open App
                  </Button>
                </Link>
              )}
              <Popover>
                <PopoverTrigger asChild>
                  <button className="relative outline-none">
                    {session.user.image ? (
                      <img
                        src={session.user.image}
                        alt={session.user.name || "User"}
                        className="w-7 h-7 sm:w-8 sm:h-8 rounded-full ring-2 ring-white/10 hover:ring-white/30 transition-all cursor-pointer"
                      />
                    ) : (
                      <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/10 flex items-center justify-center ring-2 ring-white/10 hover:ring-white/30 transition-all cursor-pointer">
                        <User className="w-4 h-4 text-white/60" />
                      </div>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2 bg-[#12151B] border-[#222836] text-white" align="end">
                  <div className="flex flex-col space-y-1">
                    {session.user.name && (
                      <div className="px-2 py-1.5 text-xs font-medium text-white/90 truncate">
                        {session.user.name}
                      </div>
                    )}
                    {session.user.email && (
                      <div className="px-2 pb-1.5 text-xs text-white/60 truncate">
                        {session.user.email}
                      </div>
                    )}
                    <div className="h-px bg-white/5 my-1" />
                    <button
                      onClick={() => signOut({
                        fetchOptions: {
                          onSuccess: () => {
                            window.location.href = "/";
                          },
                        },
                      })}
                      className="w-full flex items-center gap-2 px-2 py-1.5 text-sm text-red-400 hover:bg-white/5 rounded-md transition-colors cursor-pointer"
                    >
                      <LogOut className="w-4 h-4" />
                      Log out
                    </button>
                  </div>
                </PopoverContent>
              </Popover>
            </>
          ) : (
            <Button
              size="sm"
              onClick={() =>
                signIn.social({
                  provider: "google",
                  callbackURL: "/app",
                })
              }
              className="bg-white text-black hover:bg-white/90 font-medium"
            >
              <LogIn className="w-4 h-4 mr-2" />
              <span className="sm:hidden">Start for free</span>
              <span className="hidden sm:inline">Start for free</span>
            </Button>
          )}
        </nav>
      </div>
    </header>
  );
}
